/**
 * Save + share modals for FilterBarWithPresets.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useMemo } from 'react';

import type { SavedFilter } from '@/features/smswiser-campaigns/types/saved-filter.types';

import { getPresetFromRange } from '@/components/date-range';
import { SaveFilterModal } from '../modals/save-filter';
import type { SaveFilterModalInitial, SaveFilterSubmitData } from '../modals/save-filter';
import { ShareFilterModal } from '../modals/ShareFilterModal';

interface FilterBarPresetModalsProps {
  tab: string;
  presets: SavedFilter[];
  saveModalOpen: boolean;
  onSaveModalOpenChange: (open: boolean) => void;
  saveSubmitting: boolean;
  currentDateRangeDmy: { from: string; to: string };
  fromDateIso: string;
  toDateIso: string;
  editingInitial: SaveFilterModalInitial | null;
  onSaveSubmit: (data: SaveFilterSubmitData) => Promise<void>;
  sharingPreset: SavedFilter | null;
  shareModalOpen: boolean;
  onShareModalOpenChange: (open: boolean) => void;
  onShareUsers: (userIds: string[]) => Promise<void>;
  onUnshareUser: (userId: string) => Promise<void>;
  shareSubmitting: boolean;
}

export const FilterBarPresetModals: FC<FilterBarPresetModalsProps> = ({
  tab,
  presets,
  saveModalOpen,
  onSaveModalOpenChange,
  saveSubmitting,
  currentDateRangeDmy,
  fromDateIso,
  toDateIso,
  editingInitial,
  onSaveSubmit,
  sharingPreset,
  shareModalOpen,
  onShareModalOpenChange,
  onShareUsers,
  onUnshareUser,
  shareSubmitting,
}) => {
  const liveSharedUserIds = useMemo(() => {
    if (!sharingPreset) return [];
    const live = presets.find((p) => p.id === sharingPreset.id);
    const shared = live?.sharedWith ?? sharingPreset.sharedWith;
    return shared.map((u) => (typeof u === 'string' ? u : u.userId));
  }, [presets, sharingPreset]);

  const inferredRangePreset = useMemo(
    () => getPresetFromRange(fromDateIso, toDateIso),
    [fromDateIso, toDateIso],
  );

  return (
  <>
    <SaveFilterModal
      open={saveModalOpen}
      onOpenChange={onSaveModalOpenChange}
      onSubmit={onSaveSubmit}
      submitting={saveSubmitting}
      currentTab={tab}
      currentDateRangeDmy={currentDateRangeDmy}
      inferredRangePreset={inferredRangePreset}
      initial={editingInitial}
    />
    {sharingPreset ? (
      <ShareFilterModal
        open={shareModalOpen}
        onOpenChange={onShareModalOpenChange}
        onShare={onShareUsers}
        onUnshare={onUnshareUser}
        submitting={shareSubmitting}
        currentSharedUserIds={liveSharedUserIds}
      />
    ) : null}
  </>
  );
};
